import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, Check, MessageCircle, FileText, Package, ChevronDown, ChevronUp, Truck } from 'lucide-react';
import axios from 'axios';

const API = window.location.origin + '/api';
const STATUSES = ['pending', 'confirmed', 'preparing', 'out_for_delivery', 'delivered', 'cancelled'];
const STATUS_COLORS = { pending: '#F59E0B', confirmed: '#3B82F6', preparing: '#8B5CF6', out_for_delivery: '#F97316', delivered: '#22C55E', cancelled: '#EF4444' };

export default function AdminFulfillment() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('active');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [saving, setSaving] = useState(null);
  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    try {
      const statusParam = filter === 'active' ? '' : `?status=${filter}`;
      const { data } = await axios.get(`${API}/admin/fulfillment${statusParam}`, { withCredentials: true });
      setOrders(data);
    } catch (err) {
      console.error('Failed to fetch fulfillment orders:', err);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateItem = (orderId, itemIdx, field, value) => {
    setOrders(prev => prev.map(o => {
      if (o.id !== orderId) return o;
      const items = [...o.items];
      items[itemIdx] = { ...items[itemIdx], [field]: value };
      if (field === 'actual_weight' && items[itemIdx].price_per_kg) {
        const wt = parseFloat(value) || 0;
        items[itemIdx].actual_price = Math.round(wt * items[itemIdx].price_per_kg * 100) / 100;
        items[itemIdx].actual_unit_price = items[itemIdx].price_per_kg;
      }
      if (field === 'actual_price') {
        items[itemIdx].actual_price = parseFloat(value) || 0;
      }
      return { ...o, items };
    }));
  };

  const saveFulfillment = async (order) => {
    setSaving(order.id);
    try {
      await axios.put(`${API}/admin/fulfillment/${order.id}`, {
        items: order.items,
        status: order.status,
        fulfillment_notes: order.fulfillment_notes || '',
        delivery_fee: order.delivery_fee || 0
      }, { withCredentials: true });
      fetchOrders();
    } catch (err) {
      console.error('Failed to save fulfillment:', err);
      alert('Could not save fulfillment changes');
    }
    setSaving(null);
  };

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, { status }, { withCredentials: true });
      fetchOrders();
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Could not update order status');
    }
  };

  const openWhatsApp = async (order) => {
    const phone = (order.delivery_phone || '').replace(/[+\s-]/g, '');
    if (!phone) return;
    const items = order.items.map(i => {
      const actual = i.actual_price != null ? `K${i.actual_price.toFixed(2)}` : `K${i.subtotal?.toFixed(2) || '0'}`;
      return `${i.name} x${i.quantity} = ${actual}`;
    }).join('\n');
    const msg = `Hi ${order.delivery_name}!\n\nYour StoreRuns order #${order.id.slice(0,8)} is ready:\n\n${items}\n\nTotal: K${order.total?.toFixed(2)}\nDelivery: ${order.delivery_fee ? `K${order.delivery_fee.toFixed(2)}` : 'Free'}\n\nWe'll deliver to: ${order.delivery_address}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const downloadReceipt = (orderId) => {
    window.open(`${API}/orders/${orderId}/receipt`, '_blank');
  };

  const getOrderSubtotal = (items) => items.reduce((s, i) => s + (i.actual_price != null ? i.actual_price : (i.subtotal || 0)), 0);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="admin-page" data-testid="fulfillment-page">
      <button className="back-btn" onClick={() => navigate('/admin')}>
        <ArrowLeft /> Dashboard
      </button>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Package size={24} /> Order Fulfillment
      </h2>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {['active', ...STATUSES].map(s => (
          <button key={s} onClick={() => setFilter(s)}
            style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid var(--border-gray)', background: filter === s ? 'var(--navy)' : 'var(--white)', color: filter === s ? 'white' : 'var(--text-dark)', fontFamily: 'inherit', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer', textTransform: 'capitalize' }}>
            {s === 'active' ? 'Active Orders' : s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {loading ? <div className="loading-screen">Loading...</div> :
        orders.length === 0 ? (
          <div className="empty-state"><p>No orders in this queue</p></div>
        ) : (
          orders.map(order => {
            const expanded = expandedId === order.id;
            return (
              <div key={order.id} className="order-card" data-testid={`fulfill-order-${order.id}`}
                style={{ borderLeft: `4px solid ${STATUS_COLORS[order.status] || '#ccc'}` }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => setExpandedId(expanded ? null : order.id)}>
                  <div>
                    <span className="order-id">#{order.id.slice(0, 8)}</span>
                    <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {order.delivery_name} | {formatDate(order.created_at)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className={`order-status status-${order.status}`}>{order.status.replace(/_/g, ' ')}</span>
                    {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </div>
                </div>

                {expanded && (
                  <div style={{ marginTop: '14px' }}>
                    {/* Customer info */}
                    <div style={{ fontSize: '0.85rem', marginBottom: '10px', padding: '8px 12px', background: 'var(--light-gray)', borderRadius: '8px' }}>
                      <div><strong>Customer:</strong> {order.delivery_name} | {order.delivery_phone}</div>
                      <div><strong>Address:</strong> {order.delivery_address}</div>
                      {order.delivery_date && <div><strong>Scheduled:</strong> {order.delivery_date} {order.delivery_time_slot || ''}</div>}
                      {order.supermarket_name && <div><strong>Store:</strong> {order.supermarket_name}</div>}
                    </div>

                    {/* Items - editable */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '12px' }}>
                      <thead>
                        <tr style={{ background: 'var(--light-gray)' }}>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Item</th>
                          <th style={{ padding: '8px', textAlign: 'center' }}>Qty</th>
                          <th style={{ padding: '8px', textAlign: 'right' }}>Est. Price</th>
                          <th style={{ padding: '8px', textAlign: 'center' }}>Actual Wt (kg)</th>
                          <th style={{ padding: '8px', textAlign: 'right' }}>Actual Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((item, idx) => (
                          <tr key={`${order.id}-${item.product_id || item.name}-${idx}`} style={{ borderBottom: '1px solid var(--border-gray)' }}>
                            <td style={{ padding: '8px' }}>
                              {item.name}
                              {item.is_weighted && <Scale size={12} style={{ marginLeft: '4px', opacity: 0.5 }} />}
                              {item.weight_label && <div style={{ fontSize: '0.72rem', color: '#1E40AF' }}>{item.weight_label}</div>}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>{item.quantity}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>K{(item.subtotal || item.price * item.quantity).toFixed(2)}</td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              {item.is_weighted ? (
                                <input type="number" step="0.01" placeholder="0.00"
                                  value={item.actual_weight || ''}
                                  onChange={e => updateItem(order.id, idx, 'actual_weight', e.target.value)}
                                  style={{ width: '70px', padding: '4px 6px', borderRadius: '6px', border: '1px solid var(--border-gray)', fontSize: '0.85rem', textAlign: 'center' }}
                                  data-testid={`actual-weight-${idx}`} />
                              ) : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>
                              <input type="number" step="0.01"
                                value={item.actual_price != null ? item.actual_price : (item.subtotal || '')}
                                onChange={e => updateItem(order.id, idx, 'actual_price', e.target.value)}
                                style={{ width: '80px', padding: '4px 6px', borderRadius: '6px', border: '1px solid #1E40AF', fontSize: '0.85rem', textAlign: 'right', fontWeight: 600, color: '#1E40AF' }}
                                data-testid={`actual-price-${idx}`} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Totals */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '20px', fontSize: '0.9rem', marginBottom: '12px', flexWrap: 'wrap' }}>
                      <span>Subtotal: <strong>K{getOrderSubtotal(order.items).toFixed(2)}</strong></span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Truck size={14} /> Delivery:
                        <input type="number" step="0.01" value={order.delivery_fee || 0}
                          onChange={e => setOrders(prev => prev.map(o => o.id === order.id ? { ...o, delivery_fee: parseFloat(e.target.value) || 0 } : o))}
                          style={{ width: '60px', padding: '3px 6px', borderRadius: '6px', border: '1px solid var(--border-gray)', fontSize: '0.85rem', textAlign: 'right' }} />
                      </span>
                      <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: '1rem' }}>
                        Total: K{(getOrderSubtotal(order.items) + (order.delivery_fee || 0)).toFixed(2)}
                      </span>
                    </div>

                    {/* Notes */}
                    <textarea placeholder="Fulfillment notes (optional)..."
                      value={order.fulfillment_notes || ''}
                      onChange={e => setOrders(prev => prev.map(o => o.id === order.id ? { ...o, fulfillment_notes: e.target.value } : o))}
                      rows={2} style={{ width: '100%', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border-gray)', fontFamily: 'inherit', fontSize: '0.85rem', marginBottom: '12px', resize: 'vertical' }} />

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button onClick={() => saveFulfillment(order)} disabled={saving === order.id}
                        data-testid={`save-fulfill-${order.id}`}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 16px', borderRadius: '8px', border: 'none', background: 'var(--lime)', color: 'white', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                        <Check size={15} /> {saving === order.id ? 'Saving...' : 'Save & Update'}
                      </button>
                      <button onClick={() => openWhatsApp(order)}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 14px', borderRadius: '8px', border: 'none', background: '#25D366', color: 'white', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                        <MessageCircle size={15} /> WhatsApp Customer
                      </button>
                      <button onClick={() => downloadReceipt(order.id)}
                        style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 14px', borderRadius: '8px', border: '1px solid var(--border-gray)', background: 'var(--white)', color: 'var(--navy)', fontWeight: 500, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                        <FileText size={15} /> Receipt PDF
                      </button>
                      {/* Status buttons */}
                      {STATUSES.filter(s => s !== order.status).slice(0, 3).map(s => (
                        <button key={s} onClick={() => updateStatus(order.id, s)}
                          style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-gray)', background: 'var(--light-gray)', fontSize: '0.82rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'capitalize' }}>
                          {s.replace(/_/g, ' ')}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )
      }
    </div>
  );
}
